import { useTranslations } from "@/lib/useTranslations";
import type { ReactNode } from "react";

import { ArticleScheduleField } from "@/components/ui/admin/articles/ArticleScheduleField";
import { ArticleStatusActions } from "@/components/ui/admin/articles/ArticleStatusActions";
import type {
  ArticleEditStatusValue,
  ArticleEditor,
  ArticleStatusAction,
} from "@/core/services/types/article.types";
import { defaultLocale } from "@/i18n/config";

const STATUS_BANNER_CLASSES: Record<
  ArticleEditStatusValue,
  { container: string; icon: string; text: string }
> = {
  SIGNAL: {
    container: "bg-violet-50",
    icon: "text-violet-700",
    text: "text-violet-800",
  },
  DRAFT: {
    container: "bg-neutral-100",
    icon: "text-neutral-600",
    text: "text-neutral-800",
  },
  PENDING_REVIEW: {
    container: "bg-red-50",
    icon: "text-whart-urgent",
    text: "text-red-800",
  },
  SCHEDULED: {
    container: "bg-amber-50",
    icon: "text-whart-upcoming",
    text: "text-amber-800",
  },
  PUBLISHED: {
    container: "bg-emerald-50",
    icon: "text-emerald-700",
    text: "text-emerald-800",
  },
  ARCHIVED: {
    container: "bg-violet-50",
    icon: "text-whart-closed",
    text: "text-violet-800",
  },
};

function StatusIcon({ status }: { status: ArticleEditStatusValue }) {
  const iconClass = "size-5 shrink-0";

  switch (status) {
    case "SIGNAL":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <path d="M10 3.5a4.5 4.5 0 0 0-2.2 8.4V13h4.4v-1.1A4.5 4.5 0 0 0 10 3.5Z" />
          <path d="M8.5 13h3M9.5 15.5h1" />
        </svg>
      );
    case "DRAFT":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <path d="M5 3.5h8l2 2v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1Z" />
          <path d="M13 3.5V6h2" />
          <path d="M7 10h6M7 13h4" />
        </svg>
      );
    case "PENDING_REVIEW":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <path d="M10 6v4l2.5 2.5" />
          <circle cx="10" cy="10" r="7" />
        </svg>
      );
    case "SCHEDULED":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <rect x="4" y="5" width="12" height="11" rx="1" />
          <path d="M4 8.5h12M7 3.5v2M13 3.5v2" />
        </svg>
      );
    case "PUBLISHED":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <path d="M6.5 4.5h7l2 2v9a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1Z" />
          <path d="M13.5 4.5V7h2" />
          <path d="M8 11.5l1.5 1.5L12.5 10" />
        </svg>
      );
    case "ARCHIVED":
      return (
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={iconClass}
        >
          <rect x="4" y="4" width="12" height="3" rx="0.5" />
          <path d="M5.5 7v8.5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7" />
          <path d="M8 10.5h4" />
        </svg>
      );
  }
}

interface ArticleEditorialCycleCardProps {
  articleId: string;
  publishedAt: string | null;
  savedStatus: ArticleEditStatusValue;
  itemLabel: string;
  isStatusPending: boolean;
  pendingStatusTarget: ArticleEditStatusValue | null;
  canPublish?: boolean;
  canDelete?: boolean;
  publishDisabled?: boolean;
  saveSpinner: ReactNode;
  onStatusAction: (action: ArticleStatusAction) => void;
  onScheduleSuccess: (article: ArticleEditor) => void;
  onDeleteSignal: () => Promise<void>;
}

function formatScheduledDate(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "long",
  }).format(new Date(value));
}

export function ArticleEditorialCycleCard({
  articleId,
  publishedAt,
  savedStatus,
  itemLabel,
  isStatusPending,
  pendingStatusTarget,
  canPublish = true,
  canDelete = false,
  publishDisabled = false,
  saveSpinner,
  onStatusAction,
  onScheduleSuccess,
  onDeleteSignal,
}: ArticleEditorialCycleCardProps) {
  const t = useTranslations("articles.edit");
  const bannerStyles = STATUS_BANNER_CLASSES[savedStatus];

  return (
    <section className="space-y-4" aria-labelledby="article-editorial-cycle-label">
      <h2
        id="article-editorial-cycle-label"
        className="text-base font-semibold text-whart-text"
      >
        {t("editorialCycleLabel")}
      </h2>

      <div
        role="status"
        className={`flex flex-col gap-1 rounded px-4 py-3 ${bannerStyles.container}`}
      >
        <div className="flex items-center gap-3">
          <span className={bannerStyles.icon}>
            <StatusIcon status={savedStatus} />
          </span>
          <span className={`text-sm font-semibold ${bannerStyles.text}`}>
            {t(`statuses.${savedStatus}`)}
          </span>
        </div>
        {savedStatus === "SCHEDULED" && publishedAt ? (
          <p className={`pl-8 text-xs ${bannerStyles.text}`}>
            {t("scheduledForDate", { date: formatScheduledDate(publishedAt) })}
          </p>
        ) : null}
      </div>

      <ArticleStatusActions
        currentStatus={savedStatus}
        itemLabel={itemLabel}
        pendingTarget={pendingStatusTarget}
        isPending={isStatusPending}
        canPublish={canPublish}
        canDelete={canDelete}
        publishDisabled={publishDisabled}
        scheduleAction={
          canPublish ? (
            <ArticleScheduleField
              articleId={articleId}
              savedStatus={savedStatus}
              publishedAt={publishedAt}
              disabled={isStatusPending}
              publishDisabled={publishDisabled}
              saveSpinner={saveSpinner}
              onSuccess={onScheduleSuccess}
            />
          ) : undefined
        }
        onAction={onStatusAction}
        onDelete={onDeleteSignal}
      />
    </section>
  );
}
